import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ROUTES } from '@/lib/config/routes';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { PageSkeleton } from '@/components/common/PageSkeleton';
import { RouteError } from '@/features/misc/RouteError';
import { ConfirmProvider } from '@/components/ui/ConfirmDialog';
import { PERMISSIONS } from '@/lib/config/constants';

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const POSPage = lazy(() => import('@/features/pos/pages/POSPage'));
const ProductsPage = lazy(() => import('@/features/products/pages/ProductsPage'));
const ProductFormPage = lazy(() => import('@/features/products/pages/ProductFormPage'));
const CategoriesPage = lazy(() => import('@/features/products/pages/CategoriesPage'));
const InventoryPage = lazy(() => import('@/features/inventory/pages/InventoryPage'));
const PurchaseOrdersPage = lazy(() => import('@/features/inventory/pages/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazy(
  () => import('@/features/inventory/pages/PurchaseOrderFormPage'),
);
const SuppliersPage = lazy(() => import('@/features/inventory/pages/SuppliersPage'));
const SupplierFormPage = lazy(() => import('@/features/inventory/pages/SupplierFormPage'));
const SalesPage = lazy(() => import('@/features/sales/pages/SalesPage'));
const SaleDetailPage = lazy(() => import('@/features/sales/pages/SaleDetailPage'));
const RefundsPage = lazy(() => import('@/features/sales/pages/RefundsPage'));
const ReceivablesPage = lazy(() => import('@/features/sales/pages/ReceivablesPage'));
const RentalsPage = lazy(() => import('@/features/sales/pages/RentalsPage'));
const OrdersPage = lazy(() => import('@/features/orders/pages/OrdersPage'));
const QuotesPage = lazy(() => import('@/features/quotes/pages/QuotesPage'));
const QuoteFormPage = lazy(() => import('@/features/quotes/pages/QuoteFormPage'));
const QuoteDetailPage = lazy(() => import('@/features/quotes/pages/QuoteDetailPage'));
const CustomersPage = lazy(() => import('@/features/customers/pages/CustomersPage'));
const CustomerFormPage = lazy(() => import('@/features/customers/pages/CustomerFormPage'));
const CustomerDetailPage = lazy(() => import('@/features/customers/pages/CustomerDetailPage'));
const ExpensesPage = lazy(() => import('@/features/expenses/pages/ExpensesPage'));
const ExpenseFormPage = lazy(() => import('@/features/expenses/pages/ExpenseFormPage'));
const ExpenseEditPage = lazy(() => import('@/features/expenses/pages/ExpenseEditPage'));
const ExpenseCategoriesPage = lazy(() => import('@/features/expenses/pages/ExpenseCategoriesPage'));
const EmployeesPage = lazy(() => import('@/features/employees/pages/EmployeesPage'));
const EmployeeAdjustmentsPage = lazy(
  () => import('@/features/employees/pages/EmployeeAdjustmentsPage'),
);
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));
const UserFormPage = lazy(() => import('@/features/users/pages/UserFormPage'));
const RolesPage = lazy(() => import('@/features/roles/pages/RolesPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const ProfitReportPage = lazy(() => import('@/features/reports/pages/ProfitReportPage'));
const InventoryValuationPage = lazy(
  () => import('@/features/reports/pages/InventoryValuationPage'),
);
const TopProductsPage = lazy(() => import('@/features/reports/pages/TopProductsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const AuditLogsPage = lazy(() => import('@/features/audit-logs/pages/AuditLogsPage'));
const AccountPage = lazy(() => import('@/features/users/pages/AccountPage'));
const PartnersPage = lazy(() => import('@/features/partners/pages/PartnersPage'));
const ReviewsPage = lazy(() => import('@/features/reviews/pages/ReviewsPage'));
const DiscoverPage = lazy(() => import('@/features/discover/pages/DiscoverPage'));
const ProjectsPage = lazy(() => import('@/features/projects/pages/ProjectsPage'));
const NotFoundPage = lazy(() => import('@/features/misc/NotFoundPage'));

function Suspended({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: ROUTES.login,
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <LoginPage />
      </Suspended>
    ),
  },
  {
    path: ROUTES.forgotPassword,
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <ForgotPasswordPage />
      </Suspended>
    ),
  },
  {
    path: ROUTES.resetPassword,
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <ResetPasswordPage />
      </Suspended>
    ),
  },
  {
    path: ROUTES.root,
    errorElement: <RouteError />,
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={ROUTES.dashboard} replace /> },
      {
        path: ROUTES.dashboard,
        element: (
          <Suspended>
            <DashboardPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.pos,
        element: (
          <Suspended>
            <POSPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.products,
        element: (
          <Suspended>
            <ProductsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.productNew,
        element: (
          <Suspended>
            <ProductFormPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.productEdit(),
        element: (
          <Suspended>
            <ProductFormPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.productTopProducts,
        element: (
          <Suspended>
            <TopProductsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.categories,
        element: (
          <Suspended>
            <CategoriesPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.inventory,
        element: (
          <Suspended>
            <InventoryPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.purchaseOrders,
        element: (
          <Suspended>
            <PurchaseOrdersPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.purchaseOrderNew,
        element: (
          <Suspended>
            <PurchaseOrderFormPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.suppliers,
        element: (
          <Suspended>
            <SuppliersPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.supplierNew,
        element: (
          <Suspended>
            <SupplierFormPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.sales,
        element: (
          <Suspended>
            <SalesPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.refunds,
        element: (
          <Suspended>
            <RefundsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.receivables,
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_RECEIVABLES}>
            <Suspended>
              <ReceivablesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.rentals,
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_QUOTES}>
            <Suspended>
              <RentalsPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.saleDetail(),
        element: (
          <Suspended>
            <SaleDetailPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.orders,
        element: (
          <Suspended>
            <OrdersPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.quotes,
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_QUOTES}>
            <Suspended>
              <QuotesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.quoteNew,
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_QUOTES}>
            <Suspended>
              <QuoteFormPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.quoteEdit(),
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_QUOTES}>
            <Suspended>
              <QuoteFormPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.quoteDetail(),
        element: (
          <ProtectedRoute permission={PERMISSIONS.SALES_QUOTES}>
            <Suspended>
              <QuoteDetailPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.account,
        element: (
          <Suspended>
            <AccountPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.customers,
        element: (
          <Suspended>
            <CustomersPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.customerNew,
        element: (
          <Suspended>
            <CustomerFormPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.customerDetail(),
        element: (
          <Suspended>
            <CustomerDetailPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.expenses,
        element: (
          <ProtectedRoute permission={PERMISSIONS.EXPENSES_VIEW}>
            <Suspended>
              <ExpensesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.expenseNew,
        element: (
          <ProtectedRoute permission={PERMISSIONS.EXPENSES_CREATE}>
            <Suspended>
              <ExpenseFormPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.expenseEdit(),
        element: (
          <ProtectedRoute permission={PERMISSIONS.EXPENSES_CREATE}>
            <Suspended>
              <ExpenseEditPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.expenseCategories,
        element: (
          <ProtectedRoute permission={PERMISSIONS.EXPENSES_VIEW}>
            <Suspended>
              <ExpenseCategoriesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.employees,
        element: (
          <ProtectedRoute permission={PERMISSIONS.EMPLOYEES_VIEW}>
            <Suspended>
              <EmployeesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.employeeAdjustments(),
        element: (
          <ProtectedRoute permission={PERMISSIONS.EMPLOYEES_VIEW}>
            <Suspended>
              <EmployeeAdjustmentsPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.users,
        element: (
          <ProtectedRoute permission={PERMISSIONS.USERS_VIEW}>
            <Suspended>
              <UsersPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.userNew,
        element: (
          <ProtectedRoute permission={PERMISSIONS.USERS_CREATE}>
            <Suspended>
              <UserFormPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.userEdit(),
        element: (
          <ProtectedRoute permission={PERMISSIONS.USERS_VIEW}>
            <Suspended>
              <UserFormPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.roles,
        element: (
          <ProtectedRoute permission={PERMISSIONS.ROLES_MANAGE}>
            <Suspended>
              <RolesPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.reports,
        element: (
          <Suspended>
            <ReportsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reportSales,
        element: (
          <Suspended>
            <ReportsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reportPurchases,
        element: (
          <Suspended>
            <ReportsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reportExpenses,
        element: (
          <Suspended>
            <ReportsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reportStatement,
        element: (
          <Suspended>
            <ProfitReportPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reportInventory,
        element: (
          <Suspended>
            <InventoryValuationPage />
          </Suspended>
        ),
      },

      {
        path: '/reports/top-products',
        element: <Navigate to={ROUTES.productTopProducts} replace />,
      },
      {
        path: ROUTES.auditLogs,
        element: (
          <ProtectedRoute permission={PERMISSIONS.AUDIT_VIEW}>
            <Suspended>
              <AuditLogsPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.settings,
        element: (
          <ProtectedRoute permission={PERMISSIONS.SETTINGS_MANAGE}>
            <Suspended>
              <SettingsPage />
            </Suspended>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.partners,
        element: (
          <Suspended>
            <PartnersPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.reviews,
        element: (
          <Suspended>
            <ReviewsPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.discover,
        element: (
          <Suspended>
            <DiscoverPage />
          </Suspended>
        ),
      },
      {
        path: ROUTES.projects,
        element: (
          <Suspended>
            <ProjectsPage />
          </Suspended>
        ),
      },
    ],
  },
  {
    path: ROUTES.notFound,
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <NotFoundPage />
      </Suspended>
    ),
  },
]);

export function AppRouter() {
  return (
    <ConfirmProvider>
      <RouterProvider router={router} />
    </ConfirmProvider>
  );
}
